import React, { useState } from 'react';
import InteractiveGrid from './components/InteractiveGrid';
import LiveScoreboard from './components/LiveScoreboard';
import AlignmentTracker from './components/AlignmentTracker';
import { ValidCell } from './types';
import toast from 'react-hot-toast';

const DemoPage: React.FC = () => {
    const [selectedPosition, setSelectedPosition] = useState<ValidCell | null>(null);
    const [teamAPositions, setTeamAPositions] = useState<ValidCell[]>(['M1', 'K2', 'D3']);
    const [teamBPositions, setTeamBPositions] = useState<ValidCell[]>(['M5', 'K4', 'D1']);
    const [competitorPositions] = useState<ValidCell[]>(['M2', 'D4']);
    const [board, setBoard] = useState<{ [key in ValidCell]?: 'A' | 'B' }>({
        'M1': 'A',
        'K2': 'A',
        'D3': 'A',
        'M5': 'B',
        'K4': 'B',
        'D1': 'B'
    });

    const handleCellClick = (position: ValidCell) => {
        setSelectedPosition(position);
        toast.success(`Position ${position} sélectionnée !`, {
            icon: '🎯',
        });
    };

    const handleConfirm = () => {
        if (selectedPosition) {
            setTeamAPositions([...teamAPositions, selectedPosition]);
            setBoard({ ...board, [selectedPosition]: 'A' });
            toast.success(`Position ${selectedPosition} confirmée ! +42 pts estimés`, {
                icon: '✅',
            });
            setSelectedPosition(null);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'var(--bg-primary)',
            padding: 'var(--space-2xl)',
            color: 'var(--text-primary)'
        }}>
            {/* Header */}
            <div style={{
                marginBottom: 'var(--space-2xl)',
                textAlign: 'center'
            }}>
                <h1 style={{
                    fontSize: '3rem',
                    fontWeight: '800',
                    marginBottom: 'var(--space-md)',
                    background: 'linear-gradient(135deg, var(--team-a), #60A5FA)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                }}>
                    🚀 DÉMO - Nouveaux Composants UI
                </h1>
                <p style={{
                    fontSize: '1.25rem',
                    color: 'var(--text-secondary)',
                    maxWidth: '800px',
                    margin: '0 auto'
                }}>
                    Voici les composants modernes que j'ai créés avec animations, tooltips intelligents et design premium
                </p>
            </div>

            {/* Main Layout */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1fr',
                gap: 'var(--space-2xl)',
                maxWidth: '1600px',
                margin: '0 auto'
            }}>
                {/* Left: Interactive Grid */}
                <div>
                    <div className="glass-card" style={{
                        padding: 'var(--panel-padding)',
                        marginBottom: 'var(--space-lg)'
                    }}>
                        <h2 style={{
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            marginBottom: 'var(--space-md)'
                        }}>
                            🎮 Grille Interactive
                        </h2>
                        <p style={{
                            color: 'var(--text-secondary)',
                            marginBottom: 'var(--space-lg)',
                            fontSize: '0.875rem'
                        }}>
                            ✨ Cliquez sur une cellule → Hover pour voir le tooltip de scoring → Animations fluides
                        </p>

                        <InteractiveGrid
                            board={board}
                            onCellClick={handleCellClick}
                            selectedPosition={selectedPosition}
                            teamAPositions={teamAPositions}
                            teamBPositions={teamBPositions}
                            competitorPositions={competitorPositions}
                            currentTeam="A"
                            readOnly={false}
                        />
                    </div>

                    {/* Action Panel */}
                    <div className="glass-card" style={{
                        padding: 'var(--panel-padding)'
                    }}>
                        <h3 style={{ marginBottom: 'var(--space-md)' }}>
                            🎯 Votre Sélection
                        </h3>
                        {selectedPosition ? (
                            <div>
                                <div style={{
                                    padding: 'var(--space-lg)',
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    borderRadius: 'var(--radius-md)',
                                    border: '2px solid var(--team-a)',
                                    marginBottom: 'var(--space-md)',
                                    textAlign: 'center'
                                }}>
                                    <div style={{ fontSize: '3rem', marginBottom: 'var(--space-sm)' }}>
                                        🔵
                                    </div>
                                    <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                                        {selectedPosition}
                                    </div>
                                    <div style={{ color: 'var(--text-secondary)', marginTop: 'var(--space-xs)' }}>
                                        Position sélectionnée
                                    </div>
                                </div>
                                <button
                                    className="btn btn-success"
                                    onClick={handleConfirm}
                                    style={{ width: '100%', fontSize: '1.125rem' }}
                                >
                                    ✓ Confirmer
                                </button>
                            </div>
                        ) : (
                            <div style={{
                                padding: 'var(--space-xl)',
                                textAlign: 'center',
                                color: 'var(--text-muted)'
                            }}>
                                <div style={{ fontSize: '3rem', marginBottom: 'var(--space-sm)' }}>
                                    👆
                                </div>
                                <p>Cliquez sur une cellule de la grille pour sélectionner</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Stats Panels */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
                    {/* Live Scoreboard */}
                    <div>
                        <h2 style={{
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            marginBottom: 'var(--space-md)'
                        }}>
                            📊 Live Scoreboard
                        </h2>
                        <LiveScoreboard
                            teamAScore={142}
                            teamBScore={128}
                            teamAAlignments={1}
                            teamBAlignments={0}
                            currentRound={3}
                            maxRounds={12}
                        />
                    </div>

                    {/* Alignment Tracker */}
                    <div>
                        <h2 style={{
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            marginBottom: 'var(--space-md)'
                        }}>
                            🎯 Alignment Tracker
                        </h2>
                        <AlignmentTracker
                            horizontalAlignments={1}
                            verticalAlignments={0}
                            diagonalAlignments={0}
                            maxPossibleHorizontal={10}
                            maxPossibleVertical={4}
                            maxPossibleDiagonal={4}
                        />
                    </div>

                    {/* Features List */}
                    <div className="glass-card" style={{ padding: 'var(--panel-padding)' }}>
                        <h3 style={{ marginBottom: 'var(--space-md)' }}>
                            ✨ Fonctionnalités
                        </h3>
                        <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-sm)'
                        }}>
                            <li style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-sm)' }}>
                                <span>🖱️</span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    <strong style={{ color: 'var(--text-primary)' }}>Clic direct</strong> sur la grille
                                </span>
                            </li>
                            <li style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-sm)' }}>
                                <span>💡</span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    <strong style={{ color: 'var(--text-primary)' }}>Tooltip intelligent</strong> avec calcul en temps réel
                                </span>
                            </li>
                            <li style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-sm)' }}>
                                <span>🎨</span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    <strong style={{ color: 'var(--text-primary)' }}>États visuels</strong> (hover, selected, occupied)
                                </span>
                            </li>
                            <li style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-sm)' }}>
                                <span>⚡</span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    <strong style={{ color: 'var(--text-primary)' }}>Animations fluides</strong> Framer Motion
                                </span>
                            </li>
                            <li style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-sm)' }}>
                                <span>📊</span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    <strong style={{ color: 'var(--text-primary)' }}>Progress bars</strong> animées
                                </span>
                            </li>
                            <li style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-sm)' }}>
                                <span>🔔</span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                    <strong style={{ color: 'var(--text-primary)' }}>Notifications</strong> toast élégantes
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div className="glass-card" style={{
                padding: 'var(--panel-padding)',
                marginTop: 'var(--space-2xl)',
                maxWidth: '1600px',
                margin: '0 auto'
            }}>
                <h3 style={{ marginBottom: 'var(--space-md)' }}>
                    📝 Instructions
                </h3>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: 'var(--space-md)',
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem'
                }}>
                    <div>
                        <strong style={{ color: 'var(--success)' }}>1. Hover une cellule</strong>
                        <p style={{ margin: '4px 0 0 0' }}>
                            Le tooltip affiche le score estimé avec breakdown par bâtiment
                        </p>
                    </div>
                    <div>
                        <strong style={{ color: 'var(--team-a)' }}>2. Cliquez pour sélectionner</strong>
                        <p style={{ margin: '4px 0 0 0' }}>
                            La cellule s'anime avec un effet pulse
                        </p>
                    </div>
                    <div>
                        <strong style={{ color: 'var(--warning)' }}>3. Confirmez</strong>
                        <p style={{ margin: '4px 0 0 0' }}>
                            La position est ajoutée à votre équipe avec notification
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DemoPage;
